import RebillyAPI, {RebillyExperimentalAPI} from 'rebilly-js-sdk';
import Env from '@/common/env';
import config from '@/common/config';
import ApiHandlerDecorator from '@/common/api/api-handler-decorator';
import AllowedRequestsFilter from '@/common/api/decorators/allowed-requests-filter';
import RequestsWatcher from '@/common/api/decorators/requests-watcher';
import RequestsHistory from '@/common/api/requests-history';

const {
    VUE_APP_MODE,
    VUE_APP_PHP_DEBUG,
    VUE_APP_API_URL,
    VUE_APP_RELEASE_VERSION,
    VUE_APP_USE_WEB_STORAGE_FOR_JWT,
    VUE_APP_API_REQUEST_TIMEOUT_MS,
} = config;

/**
 * Handles the setup of a RebillyAPI instance based on the configuration defined.
 */
export default class ApiHandler {
    client;

    clientExperimental;

    compositeClient;

    isPHPDebugEnabled;

    cancelTokens = {
        default: null,
        experimental: null,
    };

    /**
     * Initialize the API handler from a set of configurations.
     * @param {Object} auth
     */
    constructor({auth}) {
        this.isPHPDebugEnabled = VUE_APP_PHP_DEBUG;
        this.auth = auth;
        this.client = null;
        this.clientExperimental = null;
        this.compositeClient = null;
        this.requestHistory = new RequestsHistory();
        this.initClient(RebillyAPI, RebillyExperimentalAPI);
        this.api = promise => promise(this.compositeClient);
    }

    cancelAllRequests() {
        this.requestHistory.purge();
    }

    initClient(api, experimentalApi) {
        const env = VUE_APP_MODE;
        const options = {
            timeout: VUE_APP_API_REQUEST_TIMEOUT_MS,
            sandbox: env === Env.sandbox,
        };
        this.client = api(options);
        this.clientExperimental = experimentalApi(options);
        this.setupInterceptors();
        this.client.setEndpoints({[env]: VUE_APP_API_URL});
        this.clientExperimental.setEndpoints({[env]: VUE_APP_API_URL});
        this.compositeClient = new ApiHandlerDecorator(
            [
                this.client,
                this.clientExperimental,
            ],
            {
                requestsWatcher: new RequestsWatcher(this.requestHistory),
            },
            {
                allowedOnly: new AllowedRequestsFilter(this.auth),
            },
        );
    }

    /**
     * Enabled the injection of special headers based on the configuration.
     * Used primarily to toggle XDEBUG session param and the API consumer header for logging.
     */
    setupInterceptors() {
        const thenDelegate = (request) => {
            if (this.isPHPDebugEnabled) {
                request.params = request.params || {};
                request.params.XDEBUG_SESSION_START = 1;
            }
            // overwrite API consumer value to identify the app
            request.headers['REB-API-CONSUMER'] = `Rebilly/Application 2@${VUE_APP_RELEASE_VERSION}`;

            if (!VUE_APP_USE_WEB_STORAGE_FOR_JWT) {
                request.withCredentials = true;
                request.xsrfCookieName = 'Xsrf-Token';
                request.xsrfHeaderName = 'Xsrf-Token';
            }

            return request;
        };
        this.client.addRequestInterceptor({thenDelegate});
        this.clientExperimental.addRequestInterceptor({thenDelegate});
    }

    /**
     * Authenticate future API calls with a JWT session token received from
     * the user store or a new login.
     * @param token
     * @param impersonate
     */
    setSessionToken(token, impersonate = false) {
        if (VUE_APP_USE_WEB_STORAGE_FOR_JWT || impersonate) {
            this.client.setSessionToken(token);
            this.clientExperimental.setSessionToken(token);
        }
    }

    /**
     * Add a interceptor to detect if the connection times out or if it
     * was aborted.
     * @param {string} timeoutMessage
     * @param {Object} store
     * @param {Function} callback
     */
    setupTimeoutErrorResponseInterceptor({timeoutMessage, store, callback}) {
        const catchDelegate = (error) => {
            // check if connection abborted or the status is unsent (0)
            if (error.code === 'ECONNABORTED') {
                // timeout problem
                callback({store, error});
                // don't throw the original error as it is not human-friendly
                throw new Error(timeoutMessage);
            }
            // otherwise let the error trickle down the execution chain
            // this is important for `404` on create for ID checks
            throw error;
        };
        this.client.addResponseInterceptor({catchDelegate});
        this.clientExperimental.addResponseInterceptor({catchDelegate});
    }

    /**
     * Add a interceptor to detect if the authorization has failed
     * @param {Object} store
     * @param {Function} callback
     */
    setupJWTErrorResponseInterceptor({store, callback}) {
        const catchDelegate = (error) => {
            if (error.response
                && error.response.status === 401
                && error.response.data
                && error.response.data.error === 'Json web token not found') {
                callback({store, error});
                throw new Error('Unable to authenticate with Rebilly, please login and try again.');
            }
            // otherwise let the error trickle down the execution chain
            throw error;
        };
        this.client.addResponseInterceptor({catchDelegate});
        this.clientExperimental.addResponseInterceptor({catchDelegate});
    }
}
